import type { DemoChatMessage } from './demoThreadSync';

export type DemoAttachment = NonNullable<DemoChatMessage['attachments']>[number];

export type DemoRenderItem =
  | { type: 'message'; message: DemoChatMessage }
  | {
      type: 'album';
      id: string;
      role: DemoChatMessage['role'];
      images: DemoAttachment[];
      messageIds: string[];
    };

function isImageAttachment(a: DemoAttachment): boolean {
  if (a.kind === 'image') return true;
  if (a.kind === 'video' || a.kind === 'audio' || a.kind === 'document') return false;
  return (a.mimeType || '').toLowerCase().startsWith('image/');
}

function hasNonImageAttachment(msg: DemoChatMessage): boolean {
  return (msg.attachments ?? []).some((a) => !isImageAttachment(a));
}

function imageAttachments(msg: DemoChatMessage): DemoAttachment[] {
  return (msg.attachments ?? []).filter(isImageAttachment);
}

/** Message is only images (no useful text, no other media). */
export function isImageOnlyMessage(msg: DemoChatMessage): boolean {
  const images = imageAttachments(msg);
  if (images.length === 0) return false;
  if (hasNonImageAttachment(msg)) return false;
  if (msg.text.trim().length > 0) return false;
  return true;
}

/**
 * Collapse consecutive same-role image-only messages into album items.
 * Messages with text (e.g. cabin name labels) stay as normal bubbles and break albums.
 */
export function groupConsecutiveImageMessages(messages: DemoChatMessage[]): DemoRenderItem[] {
  const out: DemoRenderItem[] = [];
  let i = 0;
  while (i < messages.length) {
    const msg = messages[i]!;
    if (!isImageOnlyMessage(msg)) {
      out.push({ type: 'message', message: msg });
      i += 1;
      continue;
    }

    const role = msg.role;
    const images: DemoAttachment[] = [];
    const messageIds: string[] = [];
    while (i < messages.length && messages[i]!.role === role && isImageOnlyMessage(messages[i]!)) {
      const cur = messages[i]!;
      images.push(...imageAttachments(cur));
      messageIds.push(cur.id);
      i += 1;
    }
    out.push({
      type: 'album',
      id: `album-${messageIds[0]}-${messageIds.length}`,
      role,
      images,
      messageIds,
    });
  }
  return out;
}

/** Images inside a labeled bubble (text + photos of one cabin). */
export function imagesInMessage(msg: DemoChatMessage): DemoAttachment[] {
  return imageAttachments(msg);
}
